import { FLOW_PRESETS, rekeyedFlow, type CommentFlow, type FlowPreset } from './postAutomations';

/**
 * A post's automations that are not created yet, kept in this browser (D-132).
 * Schedule and Save as draft create them; autosave does not, since creating
 * one twice makes two. Without this, leaving the page lost them. Kept per user
 * and post, dropped once created, and after a month untouched. Storage can be
 * blocked or hold anything: a flow that does not read back whole is left out.
 */

const STORAGE_KEY = 'chatflow.pending-post-automations';
export const KEEP_PENDING_FLOWS_MS = 30 * 24 * 60 * 60 * 1000;

type StoredFlow = Omit<CommentFlow, 'key'>;
type Store = Record<string, { flows: StoredFlow[]; at: number }>;

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);
const isToggle = (value: unknown, text: string): value is Record<string, unknown> =>
  isRecord(value) && typeof value.enabled === 'boolean' && typeof value[text] === 'string';

/** The flow, rebuilt from known fields only, or null when it does not read back whole. */
function flowOf(value: unknown): StoredFlow | null {
  if (!isRecord(value)) return null;
  const { preset, name, match, keywords, publicReply, followGate, opener, dm, tag } = value;
  if (typeof preset !== 'string' || !Object.hasOwn(FLOW_PRESETS, preset)) return null;
  if (name !== undefined && typeof name !== 'string') return null;
  if (match !== 'specific_words' && match !== 'any_word') return null;
  if (!Array.isArray(keywords) || !keywords.every((k) => typeof k === 'string')) return null;
  if (!isToggle(publicReply, 'text') || !isToggle(followGate, 'askText') || !isToggle(dm, 'text') || !isToggle(tag, 'name')) return null;
  if (!isRecord(opener) || typeof opener.text !== 'string' || typeof opener.buttonLabel !== 'string') return null;
  const button = dm.button;
  if (button !== null && !(isRecord(button) && typeof button.label === 'string' && typeof button.url === 'string')) return null;
  return {
    preset: preset as FlowPreset,
    ...(name !== undefined ? { name } : {}),
    match,
    keywords: [...(keywords as string[])],
    publicReply: { enabled: publicReply.enabled as boolean, text: publicReply.text as string },
    followGate: { enabled: followGate.enabled as boolean, askText: followGate.askText as string },
    opener: { text: opener.text, buttonLabel: opener.buttonLabel },
    dm: { enabled: dm.enabled as boolean, text: dm.text as string, button: button ? { label: button.label as string, url: button.url as string } : null },
    tag: { enabled: tag.enabled as boolean, name: tag.name as string },
  };
}

function readStore(): Record<string, unknown> {
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? '{}');
    return isRecord(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

const entryKey = (userId: string, postId: string) => `${userId}:${postId}`;

const isFresh = (entry: unknown, now: number): entry is { flows: unknown[]; at: number } =>
  isRecord(entry) && Array.isArray(entry.flows) && typeof entry.at === 'number' && now - entry.at <= KEEP_PENDING_FLOWS_MS;

export function readPendingFlows(userId: string, postId: string, now = Date.now()): CommentFlow[] {
  const entry = readStore()[entryKey(userId, postId)];
  if (!isFresh(entry, now)) return [];
  return entry.flows.map(flowOf).filter((flow): flow is StoredFlow => flow !== null).map(rekeyedFlow);
}

function writeStore(store: Store): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // Blocked or full: the flows then last as long as the page, as before.
  }
}

/** Drops everything kept for this user: signing out on a shared computer leaves no DM copy behind. */
export function forgetPendingFlows(userId: string): void {
  // No prototype: a stored key named `__proto__` is then a key, not a setter.
  const kept: Store = Object.create(null) as Store;
  for (const [key, entry] of Object.entries(readStore())) {
    if (!key.startsWith(`${userId}:`) && isFresh(entry, Date.now())) kept[key] = entry as Store[string];
  }
  writeStore(kept);
}

/** Keeps these flows for the post (none: forgets it), and drops entries older than a month. */
export function writePendingFlows(userId: string, postId: string, flows: CommentFlow[], now = Date.now()): void {
  const key = entryKey(userId, postId);
  const kept: Store = Object.create(null) as Store;
  for (const [other, entry] of Object.entries(readStore())) {
    if (other !== key && isFresh(entry, now)) kept[other] = entry as Store[string];
  }
  if (flows.length > 0) kept[key] = { flows: flows.map(({ key: _key, ...flow }) => flow), at: now };
  writeStore(kept);
}
